import * as React from 'react';
import { useIntl } from 'react-intl';
import { CmdCalendar } from '@commander-services/gui-components';
import { useRecoilValue } from 'recoil';
import usePrevious from '../../hooks/usePrevious';
import { IGraphResponseData } from './GraphService';
import { calendarTranslations } from '../Forms/ReactHookFormFields/Calendar';
import { widgetGraphSelectedValuesAtom } from './GraphState';

interface IProps {
  loadPartData: (dateFrom: Date, dateTo: Date) => void;
  loadData?: () => void;
  dateFrom: Date | null;
  dateTo: Date | null;
  data: IGraphResponseData[];
  isNotFilterableByRide?: boolean;
  rideId?: string | null;
}

export default function PartSelecting(props: IProps): JSX.Element {
  const { formatMessage: f } = useIntl();
  const widgetGraphSelectedValues = useRecoilValue<string[] | null>(widgetGraphSelectedValuesAtom);
  const [dateFrom, setDateFrom] = React.useState<Date | null>(props.dateFrom);
  const [dateTo, setDateTo] = React.useState<Date | null>(props.dateTo);
  const [selectedDatesFromGraph, setSelectedDatesFromGraph] = React.useState<Date[] | null>(null);
  const [dateFromError, setDateFromError] = React.useState('');
  const [dateToError, setDateToError] = React.useState('');
  const [isPart, setIsPart] = React.useState(false);
  const prevSelectedDatesFrom = usePrevious(
    selectedDatesFromGraph ? String(selectedDatesFromGraph[0]) : '',
    { jsonParse: false }
  );
  const prevSelectedDatesTo = usePrevious(
    selectedDatesFromGraph ? String(selectedDatesFromGraph[1]) : '',
    { jsonParse: false }
  );
  const prevPropsDateFrom = usePrevious(props.dateFrom?.toISOString(), { jsonParse: false });
  const prevPropsDateTo = usePrevious(props.dateTo?.toISOString(), { jsonParse: false });

  const handleNewData = () => {
    if (dateFrom && dateTo && !dateFromError && !dateToError) {
      setIsPart(true);
      props.loadPartData(dateFrom, dateTo);
    }
  };

  React.useEffect(() => {
    if (widgetGraphSelectedValues && widgetGraphSelectedValues.length === 2 && props.data) {
      const from = props.data[widgetGraphSelectedValues[0]]?.timestamp;
      const to = props.data[widgetGraphSelectedValues[1]]?.timestamp;
      if (from && to) {
        setSelectedDatesFromGraph([new Date(from), new Date(to)]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [widgetGraphSelectedValues]);

  React.useEffect(() => {
    if (
      selectedDatesFromGraph &&
      selectedDatesFromGraph.length === 2 &&
      (String(selectedDatesFromGraph[0]) !== String(dateFrom) ||
        String(selectedDatesFromGraph[1]) !== String(dateTo)) &&
      String(selectedDatesFromGraph[0]) !== String(selectedDatesFromGraph[1]) &&
      (String(selectedDatesFromGraph[0]) !== String(prevSelectedDatesFrom) ||
        String(selectedDatesFromGraph[1]) !== String(prevSelectedDatesTo))
    ) {
      const selectedDatesFrom = selectedDatesFromGraph[0];
      const selectedDatesTo = selectedDatesFromGraph[1];
      setDateFromError('');
      setDateToError('');
      if (selectedDatesFrom > selectedDatesTo) {
        setDateFrom(selectedDatesTo);
        setDateTo(selectedDatesFrom);
        props.loadPartData(selectedDatesTo, selectedDatesFrom);
      } else {
        setDateFrom(selectedDatesFrom);
        setDateTo(selectedDatesTo);
        props.loadPartData(selectedDatesFrom, selectedDatesTo);
      }

      setIsPart(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDatesFromGraph]);

  React.useEffect(() => {
    if (dateFrom && dateTo) {
      setDateFrom(props.dateFrom);
      setDateTo(props.dateTo);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.rideId]);

  React.useEffect(() => {
    if (
      dateFrom !== props.dateFrom &&
      props.dateFrom?.toISOString() !== prevPropsDateFrom &&
      dateTo !== props.dateTo &&
      props.dateTo?.toISOString() !== prevPropsDateTo
    ) {
      setDateFrom(props.dateFrom);
      setDateTo(props.dateTo);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.dateFrom, props.dateTo]);

  const handleDateFrom = (value: Date) => {
    if (props.dateFrom && value < props.dateFrom) {
      setDateFromError(f({ id: 'graph.datepicker.rangeError' }));
    } else if (dateTo && value >= dateTo) {
      setDateFromError(f({ id: 'datepicker.spanFromToValidation' }));
    } else {
      setDateFrom(value);
      setDateFromError('');
    }
  };

  const handleDateTo = (value: Date) => {
    if (props.dateTo && value > props.dateTo) {
      setDateToError(f({ id: 'graph.datepicker.rangeError' }));
    } else if (dateFrom && value <= dateFrom) {
      setDateFromError(f({ id: 'datepicker.spanFromToValidation' }));
    } else {
      setDateTo(value);
      setDateFromError('');
      setDateToError('');
    }
  };

  const loadAllData = () => {
    setIsPart(false);
    setDateFrom(null);
    setDateTo(null);
    setDateFromError('');
    setDateToError('');
    if (props.loadData) {
      props.loadData();
    }
  };

  return !props.isNotFilterableByRide ? (
    <div className="mt-4">
      <div className="row">
        <div className="col-12 col-sm-6 col-lg-10 my-3 my-lg-0">
          <div className="row">
            <div className="col-12 col-sm-4 ml-1  ">
              <CmdCalendar
                label={`${f({ id: 'graph.datepicker.from' })}:`}
                id="timeFrom"
                mode="time"
                calendarTranslations={calendarTranslations(f)}
                onChangeCallback={(value: Date | Date[]) => {
                  if (value && !Array.isArray(value)) {
                    handleDateFrom(value);
                  }
                }}
                value={dateFrom?.toISOString()}
                allowedRange={dateFrom && dateTo ? [dateFrom, dateTo] : []}
                // noLabel
                tooltip={f({ id: 'tables.tripsOverview.modal.dayTime.timeFrom.title' })}
                error={dateFromError}
                touched={true}
              />
            </div>
            <div className="col-12 col-sm-4">
              <CmdCalendar
                label={`${f({ id: 'graph.datepicker.to' })}:`}
                id="timeTo"
                mode="time"
                calendarTranslations={calendarTranslations(f)}
                onChangeCallback={(value: Date | Date[]) => {
                  if (!Array.isArray(value)) {
                    handleDateTo(value);
                  }
                }}
                value={dateTo?.toISOString()}
                error={dateToError}
                touched={true}
                //  noLabel
                tooltip={f({ id: 'tables.tripsOverview.modal.dayTime.timeTo.title' })}
              />
            </div>
          </div>
        </div>
        <div className="col-12 col-sm-6 col-lg-2 my-3 my-lg-0">
          <div
            style={{
              float: 'right',
            }}
          >
            <button
              type="button"
              style={{
                cursor: 'pointer',
                marginRight: '10px',
              }}
              className={`e-button e-button--light mb-2 ${!isPart ? 'disabled' : ''}`}
              onClick={loadAllData}
            >
              {f({
                id: 'form.cancel',
              })}
            </button>

            <button
              type="button"
              style={{
                cursor: 'pointer',
              }}
              className="e-button e-button--gray mb-2"
              onClick={handleNewData}
            >
              {f({
                id: 'form.groupChipSelectUnique.button.apply',
              })}
            </button>
          </div>
        </div>
      </div>
    </div>
  ) : (
    <div />
  );
}
